import React, { useEffect, useState } from 'react'

import { ListGrid, BackToTop, FadeIn, IntersectionVisible } from '@pb/components'
import { isAppDevice, getLabelFromStorage, isHmBrand, trackViewEvent } from '@pb/utils'
import { useSelector, useDispatch } from 'react-redux'

import { EXTERNAL_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import { getTabTracking, EVENT_TYPES, PAGE_ID, CATEGORY_PATH } from 'features/favourites/tracking'
import StyleboardNotification from 'features/Styleboard/styleboard-overview/components/StyleboardNotification'
import { filterPopularStyleboards } from 'features/Styleboard/styleboard-overview/duck/helpers'
import {
  operationFetchFollowStyleboards,
  operationFetchPopularStyleboards,
  operationFetchStyleboards,
  operationFollowStyleboard,
} from 'features/Styleboard/styleboard-overview/duck/operations'
import styleboardSelectors from 'features/Styleboard/styleboard-overview/duck/selectors'
import { NotificationType, Styleboard } from 'features/Styleboard/types'
import triggerExternalModal, { signin } from 'helpers/triggerExternalModal'

import { FollowingProps } from '../../types'
import StyleboardsError from '../StyleboardsError'
import Loader from './components/Loader'
import PopularStyleboards from './components/PopularStyleboards'
import StyleboardItem from './components/StyleboardItem'
import styles from './Following.module.scss'

const Following: React.FC<FollowingProps> = ({
  errorVariation = 'notification',
  shouldSendTrackingOnLoad = false,
}) => {
  const dispatch = useDispatch()
  const isSignedIn = useSelector(appSelectors.getIsSignedIn)

  const [followedItems, setFollowedItems] = useState<Styleboard[]>([])
  const [filteredStyleboards, setFilteredStyleboards] = useState<Styleboard[]>([])

  const followStyleboards = useSelector(styleboardSelectors.getFollowStyleboards)
  const popularStyleboards = useSelector(styleboardSelectors.getPopularStyleboards)
  const myStyleboards = useSelector(styleboardSelectors.getMyStyleboards)
  const isFollowStyleboardsLoaded = useSelector(styleboardSelectors.getIsFollowStyleboardLoaded)
  const isFollowStyleboardsFailed = useSelector(
    styleboardSelectors.getIsFollowStyleboardLoadedFailed,
  )
  const isPopularStyleboardsFailed = useSelector(styleboardSelectors.getIsPopularStyleboardsFailed)
  const isFollowingStyleboardFailed = useSelector(
    styleboardSelectors.getIsFollowingStyleboardLoadingFailed,
  )

  const nextItemsLink = useSelector(styleboardSelectors.getFollowStyleboardsNextItemsLink)
  const authorId = myStyleboards?.styleboards.length ? myStyleboards?.styleboards[0].author.id : ''

  const handleLoadMoreStyleboards = (): void => {
    if (nextItemsLink) {
      dispatch(operationFetchFollowStyleboards(nextItemsLink))
    }
  }

  const handleQuickFollow = (styleboardId): void => {
    dispatch(operationFollowStyleboard(styleboardId))

    const followedStyleboard = popularStyleboards?.filter((item) => item.id === styleboardId)

    if (followedStyleboard) {
      setFilteredStyleboards((prevStyleboards) => {
        return prevStyleboards.filter((item) => item.id !== styleboardId)
      })

      setFollowedItems((prevItems) => {
        return [...prevItems, ...followedStyleboard]
      })
    }
  }

  const handleSignIn = (): void => {
    if (isAppDevice()) {
      window.location.href = EXTERNAL_ROUTES.login
    } else {
      triggerExternalModal(signin)
    }
  }

  useEffect(() => {
    if (shouldSendTrackingOnLoad) {
      trackViewEvent(
        getTabTracking(
          EVENT_TYPES.following_styleboards_view,
          PAGE_ID.following_styleboards,
          CATEGORY_PATH.following_styleboards,
        ),
        true,
        isHmBrand,
      )
    }
  }, [])

  useEffect(() => {
    if (isSignedIn) {
      if (!myStyleboards) {
        dispatch(operationFetchStyleboards())
      }
      dispatch(operationFetchFollowStyleboards())
    }
    dispatch(operationFetchPopularStyleboards())
  }, [isSignedIn])

  useEffect(() => {
    if (popularStyleboards?.length) {
      setFilteredStyleboards(
        filterPopularStyleboards(popularStyleboards, followStyleboards?.styleboards, authorId),
      )
    }
  }, [followStyleboards, myStyleboards, popularStyleboards])

  useEffect(() => {
    // In case of error happens in following, reset to initial state
    if (isFollowingStyleboardFailed) {
      const failedStyleboard = followedItems.find(
        (styleboard) => styleboard.id === followedItems[followedItems.length - 1].id,
      )
      if (failedStyleboard) {
        setFollowedItems((prevStyleboards) =>
          prevStyleboards.filter((styleboard) => styleboard.id !== failedStyleboard?.id),
        )
        setFilteredStyleboards((prevStyleboards) => {
          return [failedStyleboard, ...prevStyleboards]
        })
      }
    }
  }, [isFollowingStyleboardFailed])

  if (!isSignedIn) {
    if (popularStyleboards) {
      return <PopularStyleboards styleboards={popularStyleboards} onQuickFollow={handleSignIn} />
    }

    if (isPopularStyleboardsFailed && errorVariation === 'messageAndNotification') {
      return <StyleboardsError titleSize="small" variation="following" />
    }
  }

  if (isFollowStyleboardsLoaded) {
    if (
      (isFollowStyleboardsFailed &&
        isPopularStyleboardsFailed &&
        errorVariation === 'messageAndNotification') ||
      (isFollowStyleboardsFailed &&
        filteredStyleboards.length === 0 &&
        errorVariation === 'messageAndNotification')
    ) {
      return <StyleboardsError titleSize="small" variation="following" />
    }

    return (
      <FadeIn>
        <ListGrid
          numberOfColumns={{
            xsmall: 1,
            small: 1,
            medium: 1,
            large: 2,
            xlarge: 3,
            xxl: 3,
          }}
          gridGap="large"
          className={styles.styleboardsContainer}
          shouldHaveItemFlex={false}
        >
          {followStyleboards?.styleboards.map((styleboard) => (
            <StyleboardItem styleboard={styleboard} />
          ))}
          {followedItems.map((styleboard) => (
            <StyleboardItem styleboard={styleboard} shouldShowCheckbox shouldShowGreenCheckbox />
          ))}
        </ListGrid>

        <IntersectionVisible onShow={handleLoadMoreStyleboards} active />
        {errorVariation === 'notification' && (
          <StyleboardNotification
            includeNotifications={[
              NotificationType.FOLLOW_STYLEBOARD_FAILED,
              NotificationType.FETCH_FOLLOW_STYLEBOARDS_FAILED,
            ]}
          />
        )}
        {filteredStyleboards.length > 0 && (
          <PopularStyleboards styleboards={filteredStyleboards} onQuickFollow={handleQuickFollow} />
        )}
        <BackToTop label={getLabelFromStorage('back.to.top.button')} />
      </FadeIn>
    )
  }

  return <Loader />
}

export default Following
